import { ApiEndpoint } from "../types";

/**
 * Utility functions for detecting and handling CM Table endpoints.
 * Centralises logic previously scattered across regex matches.
 */

/**
 * Detects if an endpoint is a v2 CM Table endpoint.
 */
export const isCmTableEndpoint = (endpoint: ApiEndpoint | null | undefined): boolean => {
  if (!endpoint) return false;

  // Primary check: the new explicit dataSourceType
  if (endpoint.dataSourceType === "cm-table") return true;

  // Fallback for transition: check the nested v2 URL pattern
  return !!endpoint.api_endpoint?.startsWith("v2/tables/") && endpoint.api_endpoint.endsWith("/rows/");
};

/**
 * Extracts the table slug from a CM Table endpoint.
 * Since the introduction of tableSlug, we prefer it over overloading tableName.
 */
export const getCmTableSlugFromEndpoint = (endpoint: ApiEndpoint | null | undefined): string | null => {
  if (!isCmTableEndpoint(endpoint)) return null;
  return endpoint?.tableSlug || endpoint?.tableName || null;
};

/** Runs of whitespace, and characters common filesystems refuse in a name. */
// eslint-disable-next-line no-control-regex
const UNSAFE_IN_FILENAME = /[\s\\/:*?"<>|\u0000-\u001f]+/g;

/**
 * The basename both CM-table Offline cells use: Raw saves `<name>.geojson` and
 * Styled saves `<name>.qml`, and QGIS pairs them by that name (qmlFilenameFor).
 * "Heritage Sites" → "Heritage_Sites"; a record export adds its id.
 */
export const cmTableExportBasename = (endpoint: ApiEndpoint, rowIds?: number[]): string => {
  const safe = (text: string) => text.replace(UNSAFE_IN_FILENAME, "_").replace(/^_+|_+$/g, "");
  const base = (safe(endpoint.name ?? "") || safe(getCmTableSlugFromEndpoint(endpoint) ?? "") || "layer").slice(0, 80);
  if (!rowIds?.length) return base;
  return rowIds.length === 1 ? `${base}_${rowIds[0]}` : `${base}_${rowIds.length}_records`;
};

/**
 * Canonical React Query key for a single CM Table row (used by useSelectedEntity's
 * CM Table fast-path). Row ids reach call sites in mixed types — strings from URL
 * deep-links (?entity_id=44), numbers from API responses / map clicks — so this
 * always coerces to string. Use this everywhere a cm-table-row key is built; calling
 * it with a differently-typed id than the live cache entry silently writes a ghost
 * entry that no subscriber reads.
 */
export const getCmTableRowQueryKey = (tableSlug: string | null | undefined, id: string | number | null | undefined) => [
  "cm-table-row",
  tableSlug,
  String(id)
];
